/**
 * Dictionary of Paya error messages mapped by reason code
 * Based on: https://docs.payaconnect.com/developers/api/test-data
 * All messages are in American English
 */

export interface PayaErrorMessage {
  code: string;
  message: string;
  userFriendlyMessage: string;
  action?: string;
}

export const payaErrorMessages: Record<string, PayaErrorMessage> = {
  "1201": {
    code: "1201",
    message: "PARTIAL_AUTH",
    userFriendlyMessage:
      "Partial authorization received. Please contact your bank or try a different payment method.",
    action:
      "Please try again with a different payment method or contact your bank.",
  },
  "1500": {
    code: "1500",
    message: "DENY",
    userFriendlyMessage:
      "Your payment was declined. Please check your card details or try a different payment method.",
    action: "Please verify your card information and try again.",
  },
  "1510": {
    code: "1510",
    message: "CALL",
    userFriendlyMessage: "Please call your bank to authorize this transaction.",
    action: "Contact your bank to approve this payment.",
  },
  "1520": {
    code: "1520",
    message: "PKUP",
    userFriendlyMessage:
      "Your card has been flagged. Please contact your bank immediately.",
    action: "Please contact your bank or use a different payment method.",
  },
  "1530": {
    code: "1530",
    message: "RETRY",
    userFriendlyMessage:
      "Transaction could not be processed. Please try again.",
    action: "Please try again in a few moments.",
  },
  "1540": {
    code: "1540",
    message: "SETUP",
    userFriendlyMessage: "Payment setup error. Please try again.",
    action: "Please try again or contact support if the problem persists.",
  },
  "1601": {
    code: "1601",
    message: "GENERICFAIL",
    userFriendlyMessage: "Payment processing failed. Please try again.",
    action: "Please try again or use a different payment method.",
  },
  "1602": {
    code: "1602",
    message: "CALL",
    userFriendlyMessage: "Please call your bank to authorize this transaction.",
    action: "Contact your bank to approve this payment.",
  },
  "1603": {
    code: "1603",
    message: "NOREPLY",
    userFriendlyMessage:
      "No response from payment processor. Please try again.",
    action: "Please try again in a few moments.",
  },
  "1604": {
    code: "1604",
    message: "PICKUP_NOFRAUD",
    userFriendlyMessage:
      "Your card has been flagged. Please contact your bank.",
    action: "Please contact your bank or use a different payment method.",
  },
  "1605": {
    code: "1605",
    message: "PICKUP_FRAUD",
    userFriendlyMessage: "Fraud prevention alert. Please contact your bank.",
    action: "Please contact your bank immediately.",
  },
  "1606": {
    code: "1606",
    message: "PICKUP_LOST",
    userFriendlyMessage:
      "Your card has been reported as lost. Please use a different card.",
    action: "Please use a different payment method.",
  },
  "1607": {
    code: "1607",
    message: "PICKUP_STOLEN",
    userFriendlyMessage:
      "Your card has been reported as stolen. Please use a different card.",
    action: "Please use a different payment method.",
  },
  "1608": {
    code: "1608",
    message: "ACCTERROR",
    userFriendlyMessage: "Account error. Please verify your card information.",
    action: "Please check your card details and try again.",
  },
  "1609": {
    code: "1609",
    message: "ALREADY_REVERSED",
    userFriendlyMessage: "This transaction has already been reversed.",
    action: "Please contact support if you need assistance.",
  },
  "1610": {
    code: "1610",
    message: "BAD_PIN",
    userFriendlyMessage: "Invalid PIN entered. Please try again.",
    action: "Please enter the correct PIN and try again.",
  },
  "1611": {
    code: "1611",
    message: "CASHBACK_EXCEEDED",
    userFriendlyMessage:
      "Cashback amount exceeded. Please try again with a lower amount.",
    action: "Please adjust the amount and try again.",
  },
  "1612": {
    code: "1612",
    message: "CASHBACK_NOAVAIL",
    userFriendlyMessage: "Cashback not available for this transaction.",
    action: "Please try again without cashback.",
  },
  "1613": {
    code: "1613",
    message: "CID_ERROR",
    userFriendlyMessage:
      "Card security code (CVV) is incorrect. Please check and try again.",
    action: "Please verify your card's security code and try again.",
  },
  "1614": {
    code: "1614",
    message: "DATE_ERROR",
    userFriendlyMessage:
      "Card expiration date is incorrect. Please check and try again.",
    action: "Please verify your card's expiration date and try again.",
  },
  "1615": {
    code: "1615",
    message: "DO NOT HONOR",
    userFriendlyMessage:
      "Your bank declined this transaction. Please contact your bank or try a different payment method.",
    action: "Please contact your bank or use a different payment method.",
  },
  "1616": {
    code: "1616",
    message: "INSUFFICIENT_FUNDS",
    userFriendlyMessage:
      "Insufficient funds. Please use a different payment method or add funds to your account.",
    action:
      "Please use a different payment method or add funds to your account.",
  },
  "1617": {
    code: "1617",
    message: "EXCEED_WITHDRAWAL_LIMIT",
    userFriendlyMessage:
      "Transaction amount exceeds your withdrawal limit. Please contact your bank.",
    action:
      "Please contact your bank to increase your limit or use a different payment method.",
  },
  "1618": {
    code: "1618",
    message: "INVALID_SERVICE_CODE",
    userFriendlyMessage: "Invalid service code. Please try again.",
    action: "Please try again or contact support.",
  },
  "1619": {
    code: "1619",
    message: "EXCEED_ACTIVITY_LIMIT",
    userFriendlyMessage:
      "Transaction limit exceeded. Please contact your bank.",
    action: "Please contact your bank or try again later.",
  },
  "1620": {
    code: "1620",
    message: "VIOLATION",
    userFriendlyMessage:
      "Transaction violation detected. Please contact your bank.",
    action: "Please contact your bank or use a different payment method.",
  },
  "1621": {
    code: "1621",
    message: "ENCRYPTION_ERROR",
    userFriendlyMessage: "Encryption error occurred. Please try again.",
    action: "Please try again or contact support if the problem persists.",
  },
  "1622": {
    code: "1622",
    message: "CARD_EXPIRED",
    userFriendlyMessage:
      "Your card has expired. Please use a different payment method.",
    action: "Please use a different payment method.",
  },
  "1623": {
    code: "1623",
    message: "REENTER",
    userFriendlyMessage: "Please re-enter your payment information.",
    action: "Please check your information and try again.",
  },
  "1624": {
    code: "1624",
    message: "SECURITY_VIOLATION",
    userFriendlyMessage:
      "Security violation detected. Please contact your bank.",
    action: "Please contact your bank or use a different payment method.",
  },
  "1625": {
    code: "1625",
    message: "NOT_PERMITTED_CARD",
    userFriendlyMessage:
      "This card type is not permitted for this transaction.",
    action: "Please use a different payment method.",
  },
  "1626": {
    code: "1626",
    message: "NOT_PERMITTED_TRAN",
    userFriendlyMessage: "This transaction type is not permitted.",
    action: "Please contact support for assistance.",
  },
  "1627": {
    code: "1627",
    message: "SYSTEM_ERROR",
    userFriendlyMessage: "System error occurred. Please try again.",
    action: "Please try again in a few moments.",
  },
  "1628": {
    code: "1628",
    message: "BAD_MERCH_ID",
    userFriendlyMessage:
      "Merchant configuration error. Please contact support.",
    action: "Please contact support for assistance.",
  },
  "1629": {
    code: "1629",
    message: "DUPLICATE_BATCH",
    userFriendlyMessage: "Duplicate batch detected. Please try again.",
    action: "Please try again or contact support.",
  },
  "1630": {
    code: "1630",
    message: "REJECTED_BATCH",
    userFriendlyMessage: "Batch was rejected. Please try again.",
    action: "Please try again or contact support.",
  },
};

/**
 * Get error message by reason code
 * @param reasonCode - Paya reason code (as string or number)
 * @returns Error message object or default message
 */
export function getPayaErrorMessage(
  reasonCode: string | number | undefined | null
): PayaErrorMessage {
  if (!reasonCode) {
    return {
      code: "UNKNOWN",
      message: "UNKNOWN_ERROR",
      userFriendlyMessage:
        "An unknown error occurred. Please try again or contact support.",
      action: "Please try again or contact support if the problem persists.",
    };
  }

  const codeStr = reasonCode.toString();
  const error = payaErrorMessages[codeStr];

  if (error) {
    return error;
  }

  // Default message for unknown codes
  return {
    code: codeStr,
    message: "UNKNOWN_ERROR",
    userFriendlyMessage: `Payment error (Code: ${codeStr}). Please try again or contact support.`,
    action: "Please try again or contact support if the problem persists.",
  };
}

/**
 * Get user-friendly error message string
 * @param reasonCode - Paya reason code
 * @returns User-friendly error message
 */
export function getPayaErrorUserMessage(
  reasonCode: string | number | undefined | null
): string {
  return getPayaErrorMessage(reasonCode).userFriendlyMessage;
}

/**
 * Map Paya status_id to user-friendly error messages
 * Based on Paya HPP status_id documentation
 * @param statusId - Paya status_id (number)
 * @returns User-friendly error message string
 */
export function getPayaMessageByStatusId(
  statusId: number | undefined | null
): string {
  if (!statusId) {
    return "Payment status unknown. Please try again or contact support.";
  }

  // Map status_id to user-friendly messages
  const statusMessages: Record<number, string> = {
    101: "Payment approved successfully.", // Should not be used as error
    102: "Payment authorized. Your payment has been authorized but not yet captured.",
    111: "Payment refunded. This transaction has been refunded.",
    121: "Address verification only. Payment requires address verification.",
    131: "Payment pending origination. Your payment is being processed.",
    132: "Payment originating. Your payment is being processed.",
    133: "Payment originated. Your payment has been initiated.",
    134: "Payment settled. Your payment has been settled.",
    135: "Payment reserved. This payment is reserved.",
    136: "Payment rejected. Your payment was rejected. Please check your card details or try a different payment method.",
    191: "Payment settled. Your payment has been settled.",
    201: "Payment voided. This transaction has been voided.",
    301: "Your payment was declined. Please check your card details or try a different payment method.",
    331: "Payment charged back. This transaction has been charged back. Please contact support.",
  };

  const message = statusMessages[statusId];
  if (message) {
    return message;
  }

  // Default message for unknown status_id
  return `Your payment was declined (Status ID: ${statusId}). Please check your card details or try a different payment method.`;
}
